import React from 'react';
import { LogOut, RefreshCw } from 'lucide-react';
import { Button } from '@/ui/primitives/Button';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { cn, useTheme } from '@/ui/theme/ThemeContext';

interface SessionRecoveryScreenProps {
  title?: string;
  message?: string;
  onReload: () => void;
  onReturnToAccount?: () => void;
}

export const SessionRecoveryScreen: React.FC<SessionRecoveryScreenProps> = ({
  title,
  message,
  onReload,
  onReturnToAccount,
}) => {
  const { theme } = useTheme();
  const { t } = useSettings();
  const resolvedTitle = title ?? t('Refreshing workspace session');
  const resolvedMessage = message ?? t('This tab is signed in, but the workspace session snapshot is stale. Reload the session to pull the latest project access, or return to your own account if an admin view-as session is no longer available.');

  return (
    <div
      data-app-ready="true"
      data-active-view="session-recovery"
      className={cn('flex min-h-screen items-center justify-center px-6 py-10', theme.bgPrimary)}
    >
      <section
        aria-labelledby="session-recovery-title"
        className={cn('w-full max-w-xl rounded-lg border p-6 shadow-sm', theme.bgSecondary, theme.borderPrimary)}
      >
        <h1 id="session-recovery-title" className={cn('text-xl font-semibold', theme.textPrimary)}>
          {resolvedTitle}
        </h1>
        <p className={cn('mt-3 text-sm leading-6', theme.textSecondary)}>
          {resolvedMessage}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button type="button" size="lg" onClick={onReload} leftIcon={<RefreshCw className="h-4 w-4" />}>
            {t('Reload session')}
          </Button>
          {onReturnToAccount ? (
            <Button type="button" size="lg" variant="outline" onClick={onReturnToAccount} leftIcon={<LogOut className="h-4 w-4" />}>
              {t('Return to my account')}
            </Button>
          ) : null}
        </div>
      </section>
    </div>
  );
};
